/**
 * The sheet you were in the middle of, kept across the app being put down.
 *
 * On a phone the back gesture does not close an app, it backgrounds it — and the browser is then
 * free to throw the page away to reclaim memory. Nothing warns you, nothing asks; you come back a
 * few minutes later and the canvas is empty, with an afternoon's arranging gone. That is not a
 * crash and it is not the person's mistake, it is simply how a web page's memory works, and the
 * only cure is to keep the work somewhere that outlives the page.
 *
 * So the studio writes the whole sheet down as it goes — every object, where it is, how big, the
 * photograph's own pixels — and reads it back when it next opens. It is deliberately NOT cleared
 * by saving, or by backgrounding, or by a reload: only by closing the app through its own close
 * button, which already asks first, or by clearing the canvas, which is the same intention said
 * a different way.
 *
 * IndexedDB rather than localStorage, because photographs are involved: localStorage holds about
 * five megabytes and throws when it is full, which is one phone picture. This is the same store
 * the local library already uses (see idb.js).
 */
import { DOCS, idbGet, idbPut, idbDelete } from './idb';

const KEY = 'session:canvas';

/** Write the sheet down. Never throws — a failed autosave must not interrupt the work. */
export async function saveSession(project) {
  try {
    await idbPut(DOCS, {
      key: KEY, collection: 'session', id: 'canvas',
      data: project, createdAt: new Date().toISOString(),
    });
    return true;
  } catch {
    return false;      // out of quota, private mode, an evicted database: carry on regardless
  }
}

/** The sheet as it was left, or null if there is none. */
export async function loadSession() {
  try { return (await idbGet(DOCS, KEY))?.data || null; } catch { return null; }
}

/** Forget it — the app was closed on purpose, or the canvas was cleared. */
export async function clearSession() {
  try { await idbDelete(DOCS, KEY); } catch { /* nothing to forget */ }
}
